import type { VideoItem } from './youtube-helpers';

import { neutralizeAncestors, restoreAncestors } from './neutralize-ancestor-styles';
import { enableYTListening, sendYTCommand } from './youtube-helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePreventBodyScroll } from '~/hooks';

import type { SavedStyle } from './neutralize-ancestor-styles';

const YOUTUBE_ORIGIN = 'https://www.youtube.com';

export const useYouTubePlayback = (videos: VideoItem[], initialIndex = 0, isVisible = true) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playedIndices, setPlayedIndices] = useState<Set<number>>(new Set());

  const inlineIframeRef = useRef<HTMLIFrameElement | null>(null);
  const currentIndexRef = useRef(currentIndex);
  const savedStylesRef = useRef<SavedStyle[]>([]);

  currentIndexRef.current = currentIndex;

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === videos.length - 1;

  usePreventBodyScroll({ enable: isFullscreen });

  // Shared exit logic — used by Escape handler and exitFullscreen
  const doExit = useCallback(() => {
    restoreAncestors(savedStylesRef.current);
    savedStylesRef.current = [];
    setIsFullscreen(false);
  }, []);

  // Detect first play per slide via YouTube postMessage API
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== YOUTUBE_ORIGIN) return;
      if (event.source !== inlineIframeRef.current?.contentWindow) return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.event !== 'infoDelivery' || data.info?.playerState !== 1) return;

        setPlayedIndices((prev) =>
          prev.has(currentIndexRef.current) ? prev : new Set(prev).add(currentIndexRef.current),
        );
      } catch {
        /* non-YouTube message */
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') doExit();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFullscreen, doExit]);

  useEffect(() => {
    !isVisible && sendYTCommand(inlineIframeRef.current, 'pauseVideo');
  }, [isVisible]);

  // Restore on unmount
  useEffect(() => () => restoreAncestors(savedStylesRef.current), []);

  const handleIframeLoad = useCallback((e: React.SyntheticEvent<HTMLIFrameElement>) => {
    enableYTListening(e.currentTarget);
  }, []);

  const setCurrentIframeRef = useCallback((el: HTMLIFrameElement | null) => {
    inlineIframeRef.current = el;
  }, []);

  const handlePrev = useCallback(() => {
    sendYTCommand(inlineIframeRef.current, 'pauseVideo');
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    sendYTCommand(inlineIframeRef.current, 'pauseVideo');
    setCurrentIndex((prev) => Math.min(videos.length - 1, prev + 1));
  }, [videos.length]);

  const enterFullscreen = useCallback(() => {
    const slide = inlineIframeRef.current?.closest(
      '.GSetupGuide-video-slide',
    ) as HTMLElement | null;
    if (!slide) return;
    savedStylesRef.current = neutralizeAncestors(slide);
    setIsFullscreen(true);
  }, []);

  return {
    currentIndex,
    isFirst,
    isLast,
    isFullscreen,
    playedIndices,
    setCurrentIframeRef,
    handleIframeLoad,
    handlePrev,
    handleNext,
    enterFullscreen,
    exitFullscreen: doExit,
  };
};
